import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Check } from 'lucide-react';
import {
  User,
  Calendar,
  AlertCircle,
  MessageSquare,
  TrendingUp,
  Shield,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

// Sample insight for preview
const sampleInsight = {
  id: "ins_abc123def",
  description: "Critical: 40.61% transaction failure rate detected",
  details: "Analysis of 40,169 November transactions reveals an alarming 40.61% failure rate (16,281 failed transactions). Primary causes include 3D Secure failures and general declines.",
  status: "new",
  priority: "high",
  category: "risk_analysis",
  type: "alert",
  assigned_to: "Risk Management Team",
  date: "2025-11-15T00:00:00Z",
  comments: [{}, {}]
};

const AIInsightDesignSelector = () => {
  const { token, selectedPlatform, backendUrl } = useAuth();
  const [selectedDesign, setSelectedDesign] = useState('current');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load saved design preference from backend
    fetchDesignPreference();
  }, [selectedPlatform, token]);

  const fetchDesignPreference = async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams();
      if (selectedPlatform && selectedPlatform !== 'all') {
        params.append('platform_id', selectedPlatform);
      }
      
      const response = await axios.get(`${backendUrl}/api/insights/design-preference?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedDesign(response.data.design_type || 'current');
    } catch (error) {
      console.error('Error loading design preference:', error);
      setSelectedDesign('current');
    } finally {
      setLoading(false);
    }
  };

  const handleDesignChange = async (designId) => {
    setSelectedDesign(designId);
    
    try {
      const payload = {
        design_type: designId,
        platform_id: selectedPlatform && selectedPlatform !== 'all' ? selectedPlatform : null
      };
      
      await axios.post(`${backendUrl}/api/insights/design-preference`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      alert(`Design changed to: ${designs.find(d => d.id === designId)?.name}. Refresh AI Insights page to see changes.`);
    } catch (error) {
      console.error('Error saving design preference:', error);
      alert('Failed to save design preference. Please try again.');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Design Components
  const CurrentDesign = () => (
    <Card className="p-5 border-l-4 border-l-red-500">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <Badge className={`${getStatusBadgeColor('new')} text-xs`}>new</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-mono">#ABC123</span>
          <Badge className={`${getPriorityBadgeColor('high')} text-xs`}>high</Badge>
        </div>
      </div>
      <h3 className="font-semibold text-base mb-2 line-clamp-2">{sampleInsight.description}</h3>
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{sampleInsight.details}</p>
      <div className="space-y-1 text-xs text-gray-500">
        <div><User className="w-3 h-3 inline mr-1" />{sampleInsight.assigned_to}</div>
        <div><Calendar className="w-3 h-3 inline mr-1" />Nov 15, 2025</div>
      </div>
    </Card>
  );

  const HorizontalDesign = () => (
    <Card className="p-4">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className="bg-red-500 text-white text-xs">HIGH</Badge>
            <Badge className={`${getStatusBadgeColor('new')} text-xs`}>NEW</Badge>
          </div>
          <h3 className="font-semibold text-sm line-clamp-1">{sampleInsight.description}</h3>
          <p className="text-xs text-gray-600 line-clamp-1">{sampleInsight.details}</p>
        </div>
        <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </div>
    </Card>
  );

  const MinimalistDesign = () => (
    <Card className="p-5 border-t-4 border-t-red-500">
      <div className="flex justify-between mb-2">
        <Badge className="bg-red-500 text-white text-xs">HIGH PRIORITY</Badge>
        <span className="text-xs font-mono text-gray-400">#ABC123</span>
      </div>
      <h3 className="font-bold text-base mb-2">{sampleInsight.description}</h3>
      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{sampleInsight.details}</p>
      <div className="flex justify-between pt-2 border-t text-xs text-gray-500">
        <span>Risk Mgmt Team</span>
        <span>Nov 15 • 2 comments</span>
      </div>
    </Card>
  );

  const ColorHeaderDesign = () => (
    <Card className="overflow-hidden">
      <div className="h-2 bg-gradient-to-r from-red-500 to-red-600"></div>
      <div className="p-4">
        <div className="flex gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={`${getPriorityBadgeColor('high')} text-xs`}>HIGH</Badge>
              <span className="text-xs text-gray-400">#ABC123</span>
            </div>
            <h3 className="font-semibold text-sm leading-tight mb-1">{sampleInsight.description}</h3>
            <p className="text-xs text-gray-600 line-clamp-2">{sampleInsight.details}</p>
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>Risk Mgmt</span>
          <span>Nov 15 • 2 💬</span>
        </div>
      </div>
    </Card>
  );

  const TicketDesign = () => (
    <Card className="p-5 border-0 shadow-lg relative overflow-hidden">
      <div className="absolute top-0 right-0 w-20 h-20 bg-red-500 opacity-5 rounded-bl-full"></div>
      <div className="relative">
        <div className="flex justify-between mb-3">
          <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">#ABC123</span>
          <Badge className="bg-red-500 text-white text-xs">HIGH</Badge>
        </div>
        <div className="flex gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Risk Analysis</span>
            <h3 className="font-bold text-base mt-1 mb-1 leading-tight">{sampleInsight.description}</h3>
            <p className="text-xs text-gray-600 line-clamp-2">{sampleInsight.details}</p>
          </div>
        </div>
        <div className="flex justify-between pt-3 border-t">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-xs">R</div>
            <span className="font-medium">Risk Mgmt</span>
          </div>
          <span className="text-xs text-blue-600">2 comments →</span>
        </div>
      </div>
    </Card>
  );

  const designs = [
    { 
      id: 'current', 
      name: 'Current Design (Left Border)', 
      component: <CurrentDesign />,
      description: 'Balanced layout with colored left border indicating priority'
    },
    { 
      id: 'horizontal', 
      name: 'Horizontal Compact', 
      component: <HorizontalDesign />,
      description: 'Space-efficient horizontal layout, best for many insights'
    },
    { 
      id: 'minimalist', 
      name: 'Minimalist (Top Border)', 
      component: <MinimalistDesign />,
      description: 'Clean, modern design with top border and minimal elements'
    },
    { 
      id: 'header', 
      name: 'Color Header', 
      component: <ColorHeaderDesign />,
      description: 'Gradient header stripe with category icon'
    },
    { 
      id: 'ticket', 
      name: 'Enhanced Ticket Style', 
      component: <TicketDesign />,
      description: 'Premium support ticket feel with gradient icon and team avatar'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Design Options Grid - Responsive */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {designs.map((design) => (
          <div key={design.id} className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base">{design.name}</h3>
                <p className="text-sm text-gray-600">{design.description}</p>
              </div>
              <Button
                variant={selectedDesign === design.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleDesignChange(design.id)}
                className="w-full sm:w-auto sm:ml-4"
              >
                {selectedDesign === design.id ? (
                  <><Check className="w-4 h-4 mr-2" /> Selected</>
                ) : (
                  'Select'
                )}
              </Button>
            </div>
            <div className="w-full max-w-md">
              {design.component}
            </div>
          </div>
        ))}
      </div>

      {/* Current Selection Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-800">
          <strong>Current Selection:</strong> {designs.find(d => d.id === selectedDesign)?.name}
        </p>
        <p className="text-sm text-blue-700 mt-1">
          <strong>Platform:</strong> {selectedPlatform === 'all' || !selectedPlatform ? 'Default (All Platforms)' : 'Current Platform'}
        </p>
        <p className="text-xs text-blue-600 mt-2">
          💡 Design is saved per platform. All users will see the selected design for this platform.
        </p>
      </div>
    </div>
  );
};

export default AIInsightDesignSelector;
